import { MapPin } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useRegions from "@/hooks/useRegions";

/**
 * Region picker for create/edit forms.
 *
 * Props:
 *   value              current region id ("" for none)
 *   onChange(id)       fired with the new region id ("" for none)
 *   suggestion         optional {id,name} shown as a subtle "Suggested" hint
 *   testId             base data-testid for the trigger
 *   compact            drops the label + wrapper (used inside filter bars)
 */
export default function RegionSelect({ value, onChange, suggestion, testId = "region-select", compact = false, placeholder = "Select region…" }) {
  const { regions } = useRegions();
  const current = value || "none";
  const suggestedId = suggestion?.id;
  const showSuggestion = suggestedId && !value;

  const selectEl = (
    <Select value={current} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger data-testid={testId} className={compact ? "h-9 text-xs" : "mt-1"}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">— No region —</SelectItem>
        {regions.map((r) => (
          <SelectItem key={r.id} value={r.id} data-testid={`${testId}-option-${r.id}`}>
            {r.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  if (compact) return selectEl;

  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
        <MapPin className="w-3 h-3" /> Region
      </Label>
      {selectEl}
      {showSuggestion && (
        <button
          type="button"
          onClick={() => onChange(suggestion.id)}
          className="mt-1 text-[11px] text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
          data-testid={`${testId}-apply-suggestion`}
        >
          Suggested: <span className="font-semibold">{suggestion.name}</span> — apply
        </button>
      )}
    </div>
  );
}
